import React, { useState, useEffect } from 'react';
import Radio from '../common/Radio';
import Input from '../common/Input';

const PaymentForm = ({ data, products, onChange }) => {
  const [paymentType, setPaymentType] = useState(data.type || 'vista');
  const [paymentMethod, setPaymentMethod] = useState(data.method || 'boleto');
  const [installments, setInstallments] = useState(data.installments || [
    { number: 1, amount: 0, date: '' },
    { number: 2, amount: 0, date: '' },
    { number: 3, amount: 0, date: '' },
  ]);

  // Calcular o valor total dos produtos
  const totalProductsValue = products.reduce(
    (total, product) => total + (product.totalPrice || 0), 
    0
  );

  useEffect(() => {
    if (paymentType === 'vista') {
      // Para pagamento à vista, apenas uma parcela com o valor total
      setInstallments([{ number: 1, amount: totalProductsValue, date: '' }]);
    } else {
      // Para pagamento a prazo, dividir o valor total em 3 parcelas iguais
      const installmentValue = totalProductsValue / 3;
      setInstallments([
        { number: 1, amount: installmentValue, date: '' },
        { number: 2, amount: installmentValue, date: '' },
        { number: 3, amount: installmentValue, date: '' },
      ]);
    }
  }, [paymentType, totalProductsValue]);

  useEffect(() => {
    onChange({
      type: paymentType,
      method: paymentMethod,
      installments: installments,
      total: totalProductsValue
    });
  }, [paymentType, paymentMethod, installments, totalProductsValue, onChange]);

  const handlePaymentTypeChange = (type) => {
    setPaymentType(type);
  };

  const handlePaymentMethodChange = (method) => {
    setPaymentMethod(method);
  };

  const handleInstallmentChange = (index, field, value) => {
    const updatedInstallments = [...installments];
    updatedInstallments[index] = {
      ...updatedInstallments[index],
      [field]: value
    };
    setInstallments(updatedInstallments);
  };

  return (
    <div className="space-y-6">
      <h2 className="text-xl font-semibold text-gray-800">Forma de Pagamento</h2>
      
      <div className="space-y-6">
        <div className="space-y-4">
          <div className="flex flex-col space-y-2">
            <span className="text-sm font-medium text-gray-700">Tipo de Pagamento</span>
            <div className="flex items-center gap-6">
              <Radio
                id="vista"
                name="paymentType"
                value="vista"
                checked={paymentType === 'vista'}
                onChange={() => handlePaymentTypeChange('vista')}
                label="À Vista"
              />
              
              <Radio
                id="prazo"
                name="paymentType"
                value="prazo"
                checked={paymentType === 'prazo'}
                onChange={() => handlePaymentTypeChange('prazo')}
                label="A Prazo"
              />
            </div>
          </div>
          
          <div className="bg-gray-50 p-4 rounded-lg">
            <h3 className="font-medium text-gray-700 mb-4">Método de Pagamento</h3>
            
            {paymentType === 'vista' ? (
              <div className="grid grid-cols-2 gap-4">
                <div className="flex items-center">
                  <input
                    id="especie"
                    name="paymentMethod"
                    type="radio"
                    className="h-4 w-4 text-primary focus:ring-primary"
                    checked={paymentMethod === 'especie'}
                    onChange={() => handlePaymentMethodChange('especie')}
                  />
                  <label htmlFor="especie" className="ml-2 block text-sm text-gray-700">
                    Em espécie
                  </label>
                </div>
                
                <div className="flex items-center">
                  <input
                    id="boleto"
                    name="paymentMethod"
                    type="radio"
                    className="h-4 w-4 text-primary focus:ring-primary"
                    checked={paymentMethod === 'boleto'}
                    onChange={() => handlePaymentMethodChange('boleto')}
                  />
                  <label htmlFor="boleto" className="ml-2 block text-sm text-gray-700">
                    Boleto bancário
                  </label>
                </div>
                
                <div className="flex items-center">
                  <input
                    id="cartao-debito"
                    name="paymentMethod"
                    type="radio"
                    className="h-4 w-4 text-primary focus:ring-primary"
                    checked={paymentMethod === 'cartao-debito'}
                    onChange={() => handlePaymentMethodChange('cartao-debito')}
                  />
                  <label htmlFor="cartao-debito" className="ml-2 block text-sm text-gray-700">
                    Cartão de débito
                  </label>
                </div>
                
                <div className="flex items-center">
                  <input
                    id="pix"
                    name="paymentMethod"
                    type="radio"
                    className="h-4 w-4 text-primary focus:ring-primary"
                    checked={paymentMethod === 'pix'}
                    onChange={() => handlePaymentMethodChange('pix')}
                  />
                  <label htmlFor="pix" className="ml-2 block text-sm text-gray-700">
                    Transferência bancária/PIX
                  </label>
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-4">
                <div className="flex items-center">
                  <input
                    id="boleto"
                    name="paymentMethod"
                    type="radio"
                    className="h-4 w-4 text-primary focus:ring-primary"
                    checked={paymentMethod === 'boleto'}
                    onChange={() => handlePaymentMethodChange('boleto')}
                  />
                  <label htmlFor="boleto" className="ml-2 block text-sm text-gray-700">
                    Boleto bancário
                  </label>
                </div>
                
                <div className="flex items-center">
                  <input
                    id="cartao-credito"
                    name="paymentMethod"
                    type="radio"
                    className="h-4 w-4 text-primary focus:ring-primary"
                    checked={paymentMethod === 'cartao-credito'}
                    onChange={() => handlePaymentMethodChange('cartao-credito')}
                  />
                  <label htmlFor="cartao-credito" className="ml-2 block text-sm text-gray-700">
                    Cartão de crédito
                  </label>
                </div>
                
                <div className="flex items-center">
                  <input
                    id="pix-mensal"
                    name="paymentMethod"
                    type="radio"
                    className="h-4 w-4 text-primary focus:ring-primary"
                    checked={paymentMethod === 'pix-mensal'}
                    onChange={() => handlePaymentMethodChange('pix-mensal')}
                  />
                  <label htmlFor="pix-mensal" className="ml-2 block text-sm text-gray-700">
                    Transferência bancária/PIX mensal
                  </label>
                </div>
              </div>
            )}
          </div>
          
          {paymentType === 'prazo' && (
            <div className="bg-gray-50 p-4 rounded-lg">
              <h3 className="font-medium text-gray-700 mb-4">Parcelas</h3>
              
              <div className="space-y-4">
                {installments.map((installment, index) => (
                  <div key={index} className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <Input
                      label={`Valor da Parcela ${installment.number}`}
                      id={`installment-amount-${index}`}
                      name={`installment-amount-${index}`}
                      type="number"
                      step="0.01"
                      min="0"
                      value={installment.amount}
                      onChange={(e) => handleInstallmentChange(index, 'amount', parseFloat(e.target.value))}
                    />
                    
                    <Input
                      label={`Data de Vencimento`}
                      id={`installment-date-${index}`}
                      name={`installment-date-${index}`}
                      type="date"
                      value={installment.date}
                      onChange={(e) => handleInstallmentChange(index, 'date', e.target.value)}
                    />
                  </div>
                ))}
              </div>
            </div>
          )}
          
          <div className="bg-gray-100 p-4 rounded-lg text-right">
            <span className="font-medium text-gray-700">Valor Total: </span>
            <span className="font-bold text-primary text-lg">R$ {totalProductsValue.toFixed(2)}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaymentForm;